/**
 * Typed icon component (doc 08 §3.11). Every icon has explicit width/height to
 * avoid the no-intrinsic-size bug the handoff README §Assets warns about.
 * Phase 1 uses a clean stroke set; the prototype's exact viewBox-14/20 maps are
 * ported during the visual-fidelity pass (Phase 8).
 */
export type IconName =
  | "dashboard"
  | "inbox"
  | "broadcast"
  | "campaign"
  | "template"
  | "contacts"
  | "automation"
  | "ai"
  | "settings"
  | "search"
  | "sun"
  | "moon"
  | "command"
  | "chevron"
  | "dot"
  | "logout";

const PATHS: Record<IconName, string> = {
  dashboard: "M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z",
  inbox: "M3 12h5l2 3h4l2-3h5M3 12l3-7h12l3 7v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
  broadcast: "M3 11v2M7 7v10M11 4v16M15 8v8M19 11v2",
  campaign: "M3 10v4h4l5 4V6l-5 4zM16 9a3 3 0 0 1 0 6",
  template: "M4 4h16v4H4zM4 11h16M4 16h10",
  contacts: "M16 19a4 4 0 0 0-8 0M12 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6",
  automation: "M5 7h9l-3-3M19 17h-9l3 3M7 12a5 5 0 0 1 10 0",
  ai: "M12 3v3M12 18v3M3 12h3M18 12h3M8 8l8 8M16 8l-8 8",
  settings: "M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6M4 12h2M18 12h2M12 4v2M12 18v2",
  search: "M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14M21 21l-5-5",
  sun: "M12 5a7 7 0 1 0 0 14 7 7 0 0 0 0-14M12 1v2M12 21v2M4 4l1 1M19 19l1 1M1 12h2M21 12h2M4 20l1-1M19 5l1-1",
  moon: "M21 12.5A8.5 8.5 0 0 1 11.5 3a7 7 0 1 0 9.5 9.5z",
  command:
    "M15 6a3 3 0 1 1 3 3h-3zM9 6a3 3 0 1 0-3 3h3zM9 18a3 3 0 1 1-3-3h3zM15 18a3 3 0 1 0 3-3h-3zM9 9h6v6H9z",
  chevron: "M6 9l6 6 6-6",
  dot: "M12 12m-4 0a4 4 0 1 0 8 0 4 4 0 1 0-8 0",
  logout:
    "M14 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-2M9 12h12M18 9l3 3-3 3",
};

const FILLED: IconName[] = ["dot"];

export function Icon({ name, size = 18 }: { name: IconName; size?: number }) {
  const filled = FILLED.includes(name);
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke={filled ? "none" : "currentColor"}
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
